import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, RotateCcw, AlertTriangle, Loader2, X, Eye } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { Pager } from "@/components/admin/Pager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtDateTime, fmtNum } from "@/lib/format";

const PAGE_SIZE = 25;

// Nombre de cada correo automático (el "kind" que guarda el backend).
const KIND: Record<string, string> = {
  registro_recibido: "Recibimos tu registro",
  puntos_acreditados: "Puntos acreditados",
  cuenta_inhabilitada: "Cuenta inhabilitada",
  cuenta_reactivada: "Cuenta reactivada",
  plan_contratado: "Plan contratado",
  plan_cancelado: "Plan cancelado",
  canje_solicitado: "Canje solicitado",
  canje_aprobado: "Canje aprobado",
  canje_rechazado: "Canje rechazado",
  canje_entregado: "Canje entregado",
  canje_nuevo_equipo: "Nuevo canje (equipo)",
  prueba: "Correo de prueba",
};
const kindLabel = (k: string) => KIND[k] || k;

const STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: "En cola", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  sent: { label: "Enviado", cls: "bg-sky-50 text-navy border-sky-200" },
  failed: { label: "No se pudo enviar", cls: "bg-red-50 text-red-700 border-red-200" },
  skipped: { label: "Sin enviar", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};
// Lo que Resend reporta después del envío.
const DELIVERY: Record<string, { label: string; cls: string }> = {
  delivered: { label: "Entregado", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  delivery_delayed: { label: "Entrega demorada", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  bounced: { label: "Rebotó", cls: "bg-red-50 text-red-700 border-red-200" },
  complained: { label: "Marcado como spam", cls: "bg-red-50 text-red-700 border-red-200" },
  failed: { label: "Falló la entrega", cls: "bg-red-50 text-red-700 border-red-200" },
  suppressed: { label: "Bloqueado por Resend", cls: "bg-red-50 text-red-700 border-red-200" },
};

const Pill = ({ s }: { s: { label: string; cls: string } }) => (
  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
);

function stateOf(m: any) {
  if (m.status === "sent" && m.delivery && DELIVERY[m.delivery]) return DELIVERY[m.delivery];
  return STATUS[m.status] || { label: m.status, cls: STATUS.skipped.cls };
}

// Registro de los correos automáticos: qué se envió, a quién y si llegó. Permite enviar uno de prueba
// y reintentar los que fallaron.
export default function AdminEmails() {
  usePageTitle("Correos enviados · Notificaciones automáticas");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("estado") || "all";
  const kind = params.get("tipo") || "all";
  const q = params.get("q") || "";
  const page = Number(params.get("pagina") || 1);
  const [search, setSearch] = useState(q);
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState<any>(null);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    if (k !== "pagina") next.delete("pagina");
    setParams(next, { replace: true });
  };

  const load = () => {
    setFailed(false);
    const p: any = { page, page_size: PAGE_SIZE };
    if (status !== "all") p.status = status;
    if (kind !== "all") p.kind = kind;
    if (q) p.q = q;
    return api.get("/admin/emails", { params: p }).then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, [status, kind, q, page]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== q) setParam("q", search.trim()); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data: r } = await api.post("/admin/emails/test", { to: testTo.trim() || null });
      toast.success(r.config?.provider ? `Correo de prueba en cola para ${r.to}` : "Correo registrado, pero el envío no está configurado");
      setTestOpen(false);
      setParam("pagina", "");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const retry = async (m: any) => {
    setBusy(m.id);
    try {
      await api.post(`/admin/emails/${m.id}/retry`);
      toast.success("El correo volvió a la cola");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const openPreview = async (m: any) => {
    setPreview({ ...m, loading: true });
    try { setPreview((await api.get(`/admin/emails/${m.id}`)).data); }
    catch (e) { toast.error(apiError(e)); setPreview(null); }
  };

  if (failed) return <LoadError what="los correos" onRetry={load} />;
  if (!data) return <PageLoader />;
  const cfg = data.config || {};
  const hasFilters = status !== "all" || kind !== "all" || !!q;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-emails">
      <div className="flex flex-wrap items-center justify-end gap-4">
        <Button onClick={() => { setTestTo(user?.email || ""); setTestOpen(true); }} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="email-test-open">
          <Send className="w-4 h-4 mr-1.5" aria-hidden="true" /> Enviar correo de prueba
        </Button>
      </div>

      {cfg.problems?.length > 0 && (
        <div className="card-obsidian p-4 border-amber-300 bg-amber-50/60 flex gap-3" role="status" data-testid="email-config-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-slate-800 space-y-1">
            <p className="font-medium">El envío de correos necesita configuración</p>
            <ul className="list-disc pl-5 text-slate-700">{cfg.problems.map((p: string) => <li key={p}>{p}</li>)}</ul>
          </div>
        </div>
      )}

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {(["sent", "pending", "failed", "skipped"] as const).map((k) => (
          <button key={k} type="button" onClick={() => setParam("estado", status === k ? "" : k)}
            className={`card-obsidian p-4 text-left transition-colors ${status === k ? "ring-2 ring-navy" : "hover:bg-slate-50"}`}
            aria-pressed={status === k} data-testid={`email-count-${k}`}>
            <dt className="text-xs uppercase tracking-wider text-slate-600">{STATUS[k].label}</dt>
            <dd className="text-2xl font-semibold text-slate-900 font-mono mt-1">{fmtNum(data.counts?.[k])}</dd>
          </button>
        ))}
      </dl>

      <div className="card-obsidian p-5">
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <div className="sm:col-span-2">
            <Label htmlFor="email-search" className="text-slate-700 text-sm">Buscar por correo o asunto</Label>
            <Input id="email-search" data-testid="email-search" value={search} onChange={(e: any) => setSearch(e.target.value)} placeholder="cliente@correo.com" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email-status" className="text-slate-700 text-sm">Estado</Label>
            <Select value={status} onValueChange={(v: string) => setParam("estado", v)}>
              <SelectTrigger id="email-status" data-testid="email-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="problem">Con problemas (rebote, spam, error)</SelectItem>
                {Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="email-kind" className="text-slate-700 text-sm">Correo</Label>
            <Select value={kind} onValueChange={(v: string) => setParam("tipo", v)}>
              <SelectTrigger id="email-kind" data-testid="email-kind-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {(data.kinds || []).map((k: string) => <SelectItem key={k} value={k}>{kindLabel(k)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setParams({}, { replace: true }); }} className="mt-3 text-slate-700" data-testid="email-clear">
            <X className="w-4 h-4 mr-1" aria-hidden="true" /> Limpiar filtros
          </Button>
        )}
      </div>

      {data.items.length === 0 ? (
        <EmptyState icon={Mail}>{hasFilters ? "No hay correos para los filtros seleccionados." : "Todavía no se ha enviado ningún correo."}</EmptyState>
      ) : (
        <div className="card-obsidian overflow-hidden">
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Tabla de correos enviados">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Para</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Correo</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Fecha (hora de Ecuador)</th>
                  <th scope="col" className="px-5 py-3"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((m: any) => (
                  <tr key={m.id} className="border-b border-slate-200 hover:bg-slate-50 align-top" data-testid="email-row">
                    <td className="px-5 py-3">
                      {m.user_id
                        ? <Link to={`/admin/usuarios/${m.user_id}`} className="text-slate-900 font-medium hover:underline">{m.to}</Link>
                        : <span className="text-slate-900 font-medium">{m.to}</span>}
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-slate-900">{kindLabel(m.kind)}</p>
                      <p className="text-xs text-slate-600">{m.subject}</p>
                    </td>
                    <td className="px-5 py-3">
                      <Pill s={stateOf(m)} />
                      {m.last_error && <p className="text-xs text-red-700 mt-1 max-w-xs break-words">{m.last_error}</p>}
                      {m.status === "pending" && m.attempts > 0 && <p className="text-xs text-slate-600 mt-1">{fmtNum(m.attempts)} intentos · próximo {fmtDateTime(m.next_attempt_at)}</p>}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{fmtDateTime(m.sent_at || m.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openPreview(m)} className="h-8 border-slate-200 text-slate-700" aria-label={`Ver el correo enviado a ${m.to}`}>
                          <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                        {(m.status === "failed" || m.status === "skipped") && (
                          <Button size="sm" variant="outline" onClick={() => retry(m)} disabled={busy === m.id} className="h-8 border-slate-200 text-slate-700" data-testid="email-retry">
                            {busy === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-label="Reintentando" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Reintentar</>}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={(p) => setParam("pagina", p > 1 ? String(p) : "")} />
        </div>
      )}

      <Dialog open={testOpen} onOpenChange={(o: boolean) => !busy && setTestOpen(o)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900" data-testid="email-test-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Enviar correo de prueba</DialogTitle>
            <DialogDescription className="text-slate-600">Sirve para comprobar que el remitente y el dominio de Resend están bien configurados.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="email-test-to" className="text-sm text-slate-700">Enviar a</Label>
            <Input id="email-test-to" type="email" value={testTo} onChange={(e: any) => setTestTo(e.target.value)} className="mt-1.5" data-testid="email-test-to" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} disabled={!!busy} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button onClick={sendTest} disabled={!!busy} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="email-test-send">
              {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Enviando" /> : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o: boolean) => !o && setPreview(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-2xl" data-testid="email-preview">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{preview?.subject}</DialogTitle>
            <DialogDescription className="text-slate-600">Para {preview?.to} · {kindLabel(preview?.kind || "")}</DialogDescription>
          </DialogHeader>
          {preview?.loading ? (
            <div className="flex justify-center py-10" role="status"><Loader2 className="w-6 h-6 animate-spin text-navy" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>
          ) : (
            <>
              {/* sandbox sin permisos: el HTML del correo no ejecuta nada ni navega dentro del panel */}
              <iframe title="Vista del correo" sandbox="" srcDoc={preview?.html || ""} className="w-full h-[420px] rounded-lg border border-slate-200 bg-white" />
              {preview?.events?.length > 0 && (
                <ul className="text-xs text-slate-600 space-y-0.5">
                  {preview.events.map((e: any) => <li key={e.id}>{fmtDateTime(e.at)} · {DELIVERY[e.type]?.label || e.type}</li>)}
                </ul>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
